import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment, useCallback, useMemo, useState } from 'react';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedList from 'I18n/components/FormattedList';
import FormattedMessage from 'I18n/components/FormattedMessage';
import getLang from 'I18n/utils/getLang';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import unescapedText from 'I18n/utils/unescapedText';
import Small from 'UIComponents/elements/Small';
import UIButton from 'UIComponents/button/UIButton';
import UILink from 'UIComponents/link/UILink';
import UIList from 'UIComponents/list/UIList';
import UIMediaObject from 'UIComponents/layout/UIMediaObject';
import UIPopover from 'UIComponents/tooltip/UIPopover';
import * as Tier from '../../constants/groupTags/Tiers';
import * as TierGroup from '../../constants/groupTags/TierGroups';
import { getPricingUrl } from '../../utils/urls';
import { getTierI18nKey, getTierHubI18nKey, getTierLevelI18nKey } from '../../utils/i18n';
import CompatibilityIcon from './CompatibilityIcon';
const TIER_GROUP_NAMES = ['CRM', 'MARKETING', 'SALES', 'SERVICE', 'CMS', 'OPERATIONS'];
export default function PlanCompatibilityMessage({
  appContext,
  compatibilityType,
  helpText,
  nonTrialHubInstallable = true,
  onFeaturesLinkClick,
  onTrackInteraction,
  popoverHeadingText = /*#__PURE__*/_jsx(FormattedMessage, {
    message: "marketplace-ui-common.compatibility.planCompatibilityMessage.compatibilityPopover.heading"
  }),
  showCompatiblePlansLink = true,
  tiers,
  tierGroupNames = TIER_GROUP_NAMES
}) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const pricingPageUrl = useMemo(() => {
    const lang = getLang();
    return getPricingUrl(appContext === 'public', '', lang);
  }, [appContext]);
  const tierGroupOrder = useMemo(() => tierGroupNames.map(tierGroupName => TierGroup[tierGroupName]), [tierGroupNames]);
  const validFlattenedTiers = useMemo(() => tierGroupOrder.reduce((final, tierGroup) => [...final, ...tierGroup], []), [tierGroupOrder]);
  const renderedTierGroups = useMemo(() => {
    if (tiers.includes(Tier.HUBSPOT_FREE)) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-ui-common.compatibility.planCompatibilityMessage.allPlans"
      });
    }

    return tierGroupOrder.reduce((final, tierGroup) => {
      const includedTiers = tierGroup.filter(tier => tiers.includes(tier));

      if (!includedTiers.length) {
        return final;
      }

      const tier = includedTiers[0];
      const tierI18nKey = getTierI18nKey(tier);

      const renderedTierLabel = /*#__PURE__*/_jsx(FormattedMessage, {
        message: tier === includedTiers[includedTiers.length - 1] ? tierI18nKey : 'marketplace-ui-common.compatibility.planCompatibilityMessage.lowestTier',
        options: {
          tier: unescapedText(tierI18nKey)
        }
      }, tier);

      return final.concat(renderedTierLabel);
    }, []);
  }, [tiers, tierGroupOrder]);
  const sortTiers = useCallback((tierA, tierB) => validFlattenedTiers.indexOf(tierA) - validFlattenedTiers.indexOf(tierB), [validFlattenedTiers]);
  const sortedTierGroups = useMemo(() => {
    const tiersToSort = [...tiers];
    return tiersToSort.sort(sortTiers).reduce((groups, tier) => {
      const [tierHub, tierLevel] = tier.split('_');

      if (!validFlattenedTiers.includes(tier)) {
        return Object.assign({}, groups);
      } else if (groups[tierHub] === undefined) {
        return Object.assign({}, groups, {
          [tierHub]: [tierLevel]
        });
      } else {
        return Object.assign({}, groups, {
          [tierHub]: groups[tierHub].concat(tierLevel)
        });
      }
    }, {});
  }, [tiers, sortTiers, validFlattenedTiers]);
  const popoverContent = useMemo(() => {
    return {
      header: /*#__PURE__*/_jsx("span", {
        className: "is--heading-7",
        children: popoverHeadingText
      }),
      body: Object.keys(sortedTierGroups).map(tierHub => /*#__PURE__*/_jsx("div", {
        className: "m-bottom-2",
        children: /*#__PURE__*/_jsxs(Small, {
          children: [/*#__PURE__*/_jsx("div", {
            style: {
              fontWeight: 600
            },
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: getTierHubI18nKey(tierHub)
            })
          }), /*#__PURE__*/_jsx(FormattedList, {
            exclusive: true,
            children: sortedTierGroups[tierHub].filter(tierLevel => isValidI18nKey(getTierLevelI18nKey(tierLevel))).map(tierLevel => /*#__PURE__*/_jsx(FormattedMessage, {
              message: getTierLevelI18nKey(tierLevel)
            }, tierLevel))
          })]
        })
      }, tierHub)),
      footer: /*#__PURE__*/_jsx(Small, {
        children: /*#__PURE__*/_jsx(UILink, {
          external: true,
          href: pricingPageUrl,
          onClick: () => onTrackInteraction('click tiers tooltip pricing link'),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-ui-common.compatibility.planCompatibilityMessage.compatibilityPopover.seeAllPlans"
          })
        })
      })
    };
  }, [pricingPageUrl, onTrackInteraction, popoverHeadingText, sortedTierGroups]);
  const compatibilityI18nKey = useMemo(() => {
    let partialCompatibilityI18nKey = '';

    if (onFeaturesLinkClick && compatibilityType !== 'COMPATIBLE_ALL' && compatibilityType !== 'COMPATIBLE_NONE') {
      partialCompatibilityI18nKey += 'features_';
    }

    switch (compatibilityType) {
      case 'COMPATIBLE_MOST':
        partialCompatibilityI18nKey += 'most_';
        break;

      case 'COMPATIBLE_SOME':
        partialCompatibilityI18nKey += 'some_';
        break;

      case 'COMPATIBLE_NONE':
        return 'none';

      default: // no default

    }

    if (appContext === 'public') {
      return 'none';
    } else {
      if (!nonTrialHubInstallable) {
        return `${partialCompatibilityI18nKey}trial`;
      } else {
        return `${partialCompatibilityI18nKey}plan`;
      }
    }
  }, [appContext, compatibilityType, nonTrialHubInstallable, onFeaturesLinkClick]);

  if (appContext === 'public') {
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(UIList, {
        children: renderedTierGroups
      }), /*#__PURE__*/_jsx(UILink, {
        "aria-label": unescapedText('marketplace-ui-common.compatibility.planCompatibilityMessage.learnMore'),
        external: true,
        href: pricingPageUrl,
        onClick: () => onTrackInteraction('click public pricing link'),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'marketplace-ui-common.compatibility.planCompatibilityMessage.ariaLabel'
        })
      })]
    });
  }

  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(UIMediaObject, {
      align: "start",
      itemLeft: /*#__PURE__*/_jsx(CompatibilityIcon, {
        compatibilityType: compatibilityType
      }),
      spacing: "extra-small",
      children: /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: `marketplace-ui-common.compatibility.planCompatibilityMessage.compatibilityDescription.${compatibilityI18nKey}_jsx`,
          options: {
            buttonProps: {
              use: 'link',
              onClick: () => {
                if (onFeaturesLinkClick) {
                  onFeaturesLinkClick();
                }

                onTrackInteraction('click partial plan compatibility');
              }
            },
            linkProps: {
              external: true,
              href: pricingPageUrl,
              onClick: () => onTrackInteraction('click account plan link')
            }
          },
          elements: {
            Button: UIButton,
            Link: UILink
          }
        }), helpText]
      })
    }), showCompatiblePlansLink && /*#__PURE__*/_jsx(UIPopover, {
      content: popoverContent,
      open: popoverOpen,
      onOpenChange: evt => setPopoverOpen(evt.target.value),
      showCloseButton: true,
      width: 250,
      children: /*#__PURE__*/_jsx(UIButton, {
        use: "link",
        onClick: () => {
          setPopoverOpen(true);
          onTrackInteraction('click see all compatible plans popover');
        },
        children: /*#__PURE__*/_jsx(Small, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-ui-common.compatibility.planCompatibilityMessage.compatibilityPopover.button"
          })
        })
      })
    })]
  });
}